import { useState, useEffect } from 'react';
import { getLeaderboard } from '../utils/api';
import { Avatar, ROLE_STYLE } from './ProfileParts';

/* Profile page — how a person appears at CPR Home Solutions.
   Forest banner with a roof-pitch pattern, portrait in a brass ring, name in Fraunces with the
   nickname as a signature, then About / fun fact on the left and this year's numbers from the
   records on the right. Editing happens in a side panel (onEdit), so the page stays a clean view. */

const CSS = `
.pv{--forest:#0D3B2E;--pine:#1A5240;--brass:#B8912E;--brass-soft:#F3EAD3;--mist:#EEF2EF;--ink:#13211B;--muted:#5E6E66;--line:#D9E2DC;
  font-family:'Inter',system-ui,-apple-system,sans-serif;color:var(--ink);max-width:1080px}
.pv-hero{position:relative;border-radius:22px;overflow:hidden;background:var(--forest);min-height:190px;
  background-image:
    linear-gradient(115deg,rgba(13,59,46,.0) 40%,rgba(184,145,46,.18) 100%),
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='44' viewBox='0 0 120 44'%3E%3Cpath d='M0 40 L30 14 L60 40 M60 40 L90 14 L120 40' fill='none' stroke='%23ffffff' stroke-opacity='.07' stroke-width='2'/%3E%3C/svg%3E");
  background-size:auto,120px 44px}
.pv-hero .co{position:absolute;left:30px;top:24px;color:rgba(255,255,255,.72);font-size:13px;font-weight:600;letter-spacing:.02em}
.pv-hero .co b{color:#fff;font-weight:700}
.pv-hero .act{position:absolute;right:24px;top:20px;display:flex;gap:8px}
.pv-btn{font:inherit;font-weight:700;font-size:14px;border-radius:999px;padding:10px 18px;cursor:pointer;border:1.5px solid rgba(255,255,255,.55);background:rgba(255,255,255,.08);color:#fff;transition:background .15s}
.pv-btn:hover{background:rgba(255,255,255,.18)}
.pv-btn.solid{background:var(--brass);border-color:var(--brass);color:#fff}
.pv-btn.solid:hover{background:#a58124}
.pv-btn:focus-visible{outline:3px solid var(--brass);outline-offset:2px}
.pv-id{display:flex;gap:26px;align-items:flex-start;padding:0 34px;margin-top:-78px;position:relative}
.pv-portrait{border-radius:50%;padding:6px;background:linear-gradient(140deg,#E7C766,var(--brass) 55%,#8A6A1C);box-shadow:0 18px 40px -18px rgba(13,59,46,.65);flex-shrink:0}
.pv-portrait>*{border:5px solid #fff}
.pv-who{padding-top:96px;min-width:0}
.pv-name{font-family:'Fraunces',Georgia,serif;font-weight:700;font-size:44px;line-height:1.02;letter-spacing:-.015em;margin:0;color:var(--ink)}
.pv-nick{font-family:'Fraunces',Georgia,serif;font-style:italic;font-weight:600;font-size:22px;color:var(--brass);margin-top:4px}
.pv-roles{display:flex;flex-wrap:wrap;gap:6px;margin-top:12px}
.pv-role{font-size:12.5px;font-weight:700;padding:5px 12px;border-radius:999px;border:1px solid transparent}
.pv-status{font-size:12.5px;font-weight:700;padding:5px 12px;border-radius:999px;background:#fff;border:1px solid var(--line);color:var(--muted)}
.pv-status i{display:inline-block;width:7px;height:7px;border-radius:50%;margin-right:6px;vertical-align:1px}
.pv-body{display:grid;grid-template-columns:minmax(0,1.6fr) minmax(260px,1fr);gap:22px;margin-top:30px}
.pv-sec{background:#fff;border:1px solid var(--line);border-radius:18px;padding:26px 28px}
.pv-sec+.pv-sec{margin-top:22px}
.pv-h{font-family:'Fraunces',Georgia,serif;font-weight:700;font-size:21px;margin:0 0 12px;color:var(--ink)}
.pv-bio{font-size:16.5px;line-height:1.7;color:#24342C;max-width:62ch;white-space:pre-wrap;margin:0}
.pv-empty{color:var(--muted);font-size:15px;line-height:1.6}
.pv-empty button{font:inherit;font-weight:700;color:var(--forest);background:none;border:0;padding:0;cursor:pointer;text-decoration:underline;text-underline-offset:3px}
.pv-fact{position:relative;background:var(--brass-soft);border:0;padding:26px 28px 26px 72px}
.pv-fact:before{content:"\\201C";position:absolute;left:22px;top:6px;font-family:'Fraunces',Georgia,serif;font-size:76px;line-height:1;color:var(--brass)}
.pv-fact p{font-family:'Fraunces',Georgia,serif;font-size:20px;line-height:1.45;margin:0;color:#3D2F0B}
.pv-fact small{display:block;margin-top:8px;font-size:13px;color:#7A6427;font-weight:600}
.pv-stats{display:grid;grid-template-columns:1fr 1fr;gap:0;border-top:1px solid var(--line);margin-top:6px}
.pv-stat{padding:16px 0 4px}
.pv-stat:nth-child(odd){border-right:1px solid var(--line);padding-right:16px}
.pv-stat:nth-child(even){padding-left:18px}
.pv-stat b{display:block;font-family:'Fraunces',Georgia,serif;font-size:30px;font-weight:700;line-height:1.05;font-variant-numeric:tabular-nums;color:var(--forest)}
.pv-stat span{font-size:13px;color:var(--muted)}
.pv-rank{margin-top:16px;display:flex;align-items:center;gap:12px;background:var(--mist);border-radius:12px;padding:12px 14px;font-size:14px}
.pv-rank b{font-family:'Fraunces',Georgia,serif;font-size:24px;color:var(--brass);min-width:44px;text-align:center}
.pv-dl{display:grid;grid-template-columns:auto 1fr;gap:10px 18px;margin:0;font-size:14.5px}
.pv-dl dt{color:var(--muted)}
.pv-dl dd{margin:0;font-weight:600;text-align:right;min-width:0;overflow-wrap:anywhere}
.pv-note{font-size:13px;color:var(--muted);margin-top:14px;line-height:1.5}
@media(max-width:860px){.pv-body{grid-template-columns:1fr}.pv-name{font-size:34px}}
@media(max-width:600px){
  .pv-hero{min-height:150px}.pv-hero .co{left:20px;top:18px;right:150px}.pv-hero .act{right:14px;top:12px}
  .pv-id{flex-direction:column;align-items:flex-start;gap:12px;padding:0 20px;margin-top:-64px}.pv-who{padding-top:0}
  .pv-name{font-size:30px}.pv-sec{padding:20px}.pv-fact{padding:20px 20px 20px 58px}.pv-fact:before{left:14px}
}
@media(prefers-reduced-motion:reduce){.pv *{transition:none!important}}
`;

const money0 = v => '$' + Math.round(Number(v) || 0).toLocaleString('en-US');
const BOARDS = [['salesReps', 'Sales Rep'], ['telemarketers', 'Telemarketer'], ['canvassers', 'Canvasser'], ['confirmers', 'Confirmer']];

// This year's numbers for this person, straight from the records (same rules as the Leaderboard)
function useYearNumbers(name) {
  const [n, setN] = useState(null);
  useEffect(() => {
    if (!name) return;
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
    getLeaderboard(today.slice(0, 4) + '-01-01', today).then(r => {
      let best = null;
      for (const [k, role] of BOARDS) {
        const list = r[k] || [];
        const i = list.findIndex(p => p.name.toLowerCase() === name.trim().toLowerCase());
        if (i < 0) continue;
        const p = list[i];
        if (!best || p.revenue > best.revenue) best = { ...p, role, rank: p.jobs ? i + 1 : null, of: list.length };
      }
      setN(best || { jobs: 0, revenue: 0 });
    }).catch(() => setN({ failed: true }));
  }, [name]);
  return n;
}

function useBrandFonts() {   // Fraunces + Inter, the same fonts the Pace Board uses
  useEffect(() => {
    const id = 'cpr-serif-fonts';
    if (document.getElementById(id)) return;
    const l = document.createElement('link'); l.id = id; l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Fraunces:ital,opsz,wght@0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,600&family=Inter:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(l);
  }, []);
}

/* Side panel for editing — slides in from the right over the profile */
export function EditPanel({ title, onClose, children }) {
  useEffect(() => {
    const k = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', k); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div className="pv-panel-wrap" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{PANEL_CSS}</style>
      <aside className="pv-panel" role="dialog" aria-label={title}>
        <header><h2>{title}</h2><button type="button" onClick={onClose} aria-label="Close">✕</button></header>
        <div className="pv-panel-body">{children}</div>
      </aside>
    </div>
  );
}
const PANEL_CSS = `
.pv-panel-wrap{position:fixed;inset:0;z-index:1000;background:rgba(9,30,23,.42);display:flex;justify-content:flex-end;animation:pvFade .18s ease-out}
.pv-panel{width:min(560px,100%);height:100%;background:#fff;display:flex;flex-direction:column;box-shadow:-24px 0 60px -30px rgba(0,0,0,.5);animation:pvSlide .26s cubic-bezier(.2,.8,.2,1)}
.pv-panel header{display:flex;align-items:center;justify-content:space-between;padding:20px 26px;border-bottom:1px solid #D9E2DC}
.pv-panel header h2{margin:0;font-family:'Fraunces',Georgia,serif;font-size:22px;font-weight:700;color:#13211B}
.pv-panel header button{width:38px;height:38px;border-radius:50%;border:1px solid #D9E2DC;background:#fff;cursor:pointer;font-size:15px}
.pv-panel-body{flex:1;overflow-y:auto;padding:24px 26px 30px}
@keyframes pvSlide{from{transform:translateX(40px);opacity:.4}}
@keyframes pvFade{from{opacity:0}}
@media(prefers-reduced-motion:reduce){.pv-panel,.pv-panel-wrap{animation:none}}
`;

export default function ProfileView({ person, onEdit, editLabel = 'Edit profile', self = false, extraActions }) {
  useBrandFonts();
  const n = useYearNumbers(person.name);
  const year = new Date().getFullYear();
  const active = person.status !== 'Inactive';
  const firstName = (person.name || '').split(/\s+/)[0];

  return (
    <div className="pv">
      <style>{CSS}</style>
      <div className="pv-hero">
        <div className="co"><b>CPR Home Solutions</b> &nbsp;team member</div>
        <div className="act">
          {extraActions}
          {onEdit && <button type="button" className="pv-btn solid" onClick={onEdit}>{editLabel}</button>}
        </div>
      </div>

      <div className="pv-id">
        <div className="pv-portrait"><Avatar name={person.name} photo={person.photo} size={168} /></div>
        <div className="pv-who">
          <h1 className="pv-name">{person.name}</h1>
          {person.nickname && <div className="pv-nick">“{person.nickname}”</div>}
          <div className="pv-roles">
            {person.roles.map(r => { const s = ROLE_STYLE[r] || { bg: '#eee', fg: '#333', icon: '' }; return (
              <span key={r} className="pv-role" style={{ background: s.bg, color: s.fg }}>{s.icon} {r}</span>); })}
            <span className="pv-status"><i style={{ background: active ? '#2E9B5F' : '#B0B8B4' }} />{active ? 'Active' : 'Inactive'}</span>
          </div>
        </div>
      </div>

      <div className="pv-body">
        <div>
          <section className="pv-sec">
            <h2 className="pv-h">About {self ? 'me' : firstName}</h2>
            {person.bio
              ? <p className="pv-bio">{person.bio}</p>
              : <div className="pv-empty">{self
                  ? <>Tell the team what you do at CPR: your trade, your area, how long you've been here. <button type="button" onClick={onEdit}>Write your bio</button></>
                  : 'No bio yet.'}</div>}
          </section>
          {person.fun_fact ? (
            <section className="pv-sec pv-fact">
              <p>{person.fun_fact}</p>
              <small>Fun fact about {self ? 'me' : firstName}</small>
            </section>
          ) : self && (
            <section className="pv-sec">
              <h2 className="pv-h">Fun fact</h2>
              <div className="pv-empty">Something people wouldn't guess about you. <button type="button" onClick={onEdit}>Add a fun fact</button></div>
            </section>
          )}
        </div>

        <div>
          <section className="pv-sec">
            <h2 className="pv-h">{year} so far</h2>
            {!n ? <div className="pv-empty">Loading numbers…</div>
              : n.failed ? <div className="pv-empty">Numbers aren't available right now.</div>
              : (
                <>
                  <div className="pv-stats">
                    <div className="pv-stat"><b>{n.jobs}</b><span>job{n.jobs === 1 ? '' : 's'} sold</span></div>
                    <div className="pv-stat"><b>{money0(n.revenue)}</b><span>revenue</span></div>
                    <div className="pv-stat"><b>{n.jobs ? money0(n.avg) : '—'}</b><span>average job</span></div>
                    <div className="pv-stat"><b>{n.shared || 0}</b><span>shared jobs</span></div>
                  </div>
                  {n.rank && <div className="pv-rank"><b>#{n.rank}</b><span>on the {n.role.toLowerCase()} leaderboard this year, out of {n.of}</span></div>}
                  <div className="pv-note">From jobs sold this year, cancelled jobs left out. Shared jobs count at the split.</div>
                </>
              )}
          </section>
          <section className="pv-sec">
            <h2 className="pv-h">Details</h2>
            <dl className="pv-dl">
              <dt>Main role</dt><dd>{person.role}</dd>
              {person.roles.length > 1 && <><dt>Also works as</dt><dd>{person.roles.filter(r => r !== person.role).join(', ')}</dd></>}
              <dt>Nickname</dt><dd>{person.nickname || 'Not set'}</dd>
              {person.login && <><dt>Login</dt><dd>{person.login.username}</dd></>}
              <dt>Status</dt><dd>{active ? 'Active' : 'Inactive'}</dd>
            </dl>
            {self && <div className="pv-note">Your name, nickname and roles are set by your manager.</div>}
          </section>
        </div>
      </div>
    </div>
  );
}
